/**
 * Help Center: "Getting started" section articles. Format and rules:
 * src/content/help/README.md.
 */
export default [
  {
    slug: 'what-is-defera',
    title: 'What is Defera?',
    summary: 'Defera lets you book a flight, or a flight and hotel, and pay for it over time. There’s no credit check and no interest, and your trip price is secured.',
    keywords: ['about defera', 'layaway', 'pay over time', 'pay later', 'how it works'],
    related: ['getting-started/how-defera-works', 'getting-started/installments-vs-zero', 'getting-started/price-protection', 'getting-started/credit-check'],
    updated: '2026-10-05',
    body: `Defera lets you book a flight, or a flight and hotel together, and pay for it over time. There's no credit check and no interest.

You pay a small part of your trip when you book, or nothing at all with Defera Zero. The rest is paid in automatic weekly or bi-weekly payments. Once your booking is approved, your trip price is secured, so if airfares go up while you're paying, your price doesn't.

## How paying over time works

If you've never paid for a trip this way before, here's what happens:

1. **You choose your trip.** Search for flights on **Book**, or tap **Stays** in the search to book a flight and hotel together. Then pick how you'd like to pay.
2. **You pay a deposit, or nothing at all.** A deposit is the part of your trip price you pay upfront, when you book. With Defera Installments, it can be as low as 5% on trips more than 90 days away. With Defera Zero, nothing is due when you book.
3. **Your trip price is secured.** Once your booking is approved, your agreed-upon trip price is protected from future airfare increases, as long as you stay in good standing on your payment plan. Good standing means keeping up with your payments.
4. **The rest is paid automatically.** Your remaining balance is split into weekly or bi-weekly payments. Each one is charged on its date to your card on file, which is the debit or credit card you save when you book. You don't have to make each payment yourself.
5. **You get your confirmation number.** Once your trip is paid in full, our team releases your airline confirmation number, usually within 24 hours on weekdays. It's the code you'll use to check in with the airline.

## Two ways to pay

Defera has two payment plans. The biggest differences are what's due when you book and which fares each plan works on:

| | Defera Installments | Defera Zero |
|---|---|---|
| Due when you book | A deposit, as low as 5% on trips more than 90 days away | Nothing |
| Which fares | Any fare on the airlines we support | Refundable fares on select airlines |
| Your payments | Weekly or bi-weekly | Equal payments, weekly or bi-weekly |
| Flight + Hotel trips | Yes | No |

In short, Defera Zero asks for nothing upfront but works only on certain fares. Defera Installments works on any fare on the airlines we support, including Flight + Hotel trips. If you're not sure which fits, [Installments or Defera Zero: which should I choose?](/help/getting-started/installments-vs-zero) compares them in more detail.

## What's the same on both plans

- **Your price is secured.** Once your booking is approved, your agreed-upon trip price is protected from future airfare increases while you stay in good standing.
- **No credit check.** We never check your credit to let you book.
- **No interest and no hidden fees.** Your checkout page shows exactly what you'll pay before you book.
- **No penalty for paying early.** You can pay ahead or pay off your trip at any time.

## When do I get my confirmation number?

Your confirmation number is the code the airline uses to find your reservation. Because it comes once your trip is paid in full, it's completely normal for your trip to show a payment schedule and no confirmation number until then. Nothing is wrong with your booking, and your price is already protected.

If you'd like it sooner, you can pay ahead or pay off your balance early, with no penalty. For the full timing, see [When do I get my confirmation number?](/help/confirmation-and-travel/when-confirmation-number)

## Why we built Defera

We started Defera in 2024 in San Francisco with a simple idea: travel shouldn't depend on perfect credit or a big bank balance. Defera is built for people that traditional travel financing leaves out, including gig workers, students, people rebuilding their credit and families on a tight budget.

> [!TIP] New here? [How Defera works, step by step](/help/getting-started/how-defera-works) follows a booking from your first search to takeoff.`,
  },
  {
    slug: 'how-defera-works',
    title: 'How Defera works, step by step',
    summary: 'Find your trip, choose how to pay and book. Your payments are charged automatically, and your confirmation number comes once your trip is paid in full.',
    keywords: ['booking process', 'what happens after I book', 'payment plan', 'confirmation number', 'pay over time', 'getting started'],
    related: ['getting-started/installments-vs-zero', 'booking/checkout-page', 'booking/after-you-book', 'confirmation-and-travel/when-confirmation-number'],
    updated: '2026-10-05',
    body: `With Defera, you book your trip now and pay for it over time. You choose a flight, or a flight and hotel, pick how you'd like to pay, and book. Your payments are then charged automatically until your trip is paid in full, and that's when your airline confirmation number is released.

Your trip price is secured much earlier, once your booking is approved. Here's the whole journey, from your first search to takeoff.

## From search to takeoff

1. **You search for your trip.** On **Book**, search for flights to any destination worldwide, one-way or round trip, in Economy, Premium Economy, Business or First. To add a hotel, tap **Stays** at the top of the search before you start. The calendar starts {{PLAN_MIN_DAYS}} days from today, and closer dates can't be booked.
2. **You choose how to pay.** Defera Installments, our default plan, starts with a deposit, which is the part of your trip price you pay upfront when you book. Defera Zero has nothing due when you book, and your checkout page offers it when your fare qualifies.
3. **You review your trip and book.** Your checkout page shows what's due today, then your payments and the date of the last one, then your trip total. The **Price Breakdown** lists every cost. You agree to the acknowledgments with one checkbox, and you can tap **Read the full terms** to see them in full.
4. **You get your booking confirmation.** We email you the moment you book, and your trip appears in **Trips**. That's where you'll find your payment schedule and everything else for your trip.
5. **Your payments are charged automatically.** Each weekly or bi-weekly payment is charged on its date to your card on file, which is the debit or credit card saved for your payments. You can pay ahead or pay off early at any time, with no penalty.
6. **You get your confirmation number.** Once your trip is paid in full, our team releases your airline confirmation number, usually within 24 hours on weekdays. It appears on your trip page in **Trips**, and we email you when it's ready.
7. **You travel.** Use your confirmation number to check in with the airline as usual.

## When is my price secured?

Once your booking is approved, your agreed-upon trip price is protected from future airfare increases, as long as you stay in good standing on your payment plan. Good standing means keeping up with your payments.

Until you book, fares can change. **Email me this plan**, under the pay button, sends you the figures on your screen with a link back to the same checkout. It's handy if you'd like time to think, but it doesn't hold the price. To see what price protection means day to day, read [How your trip price is protected](/help/getting-started/price-protection).

## What can I do while I'm paying?

Your trip page has what you need while you pay. Open **Trips** and tap your trip. From there, you can:

- Add seats or checked bags at any time, from **Flight Options**, the menu at the top right of the trip's **Flight Details** section, with **Purchase Seats** or **Purchase Baggage**.
- Pay ahead, or pay off your trip early, with **Make a Payment**.
- Switch between weekly and bi-weekly payments with **Change Schedule**, on plans that show this option.
- Change the card your payments are charged to with **Update Card**.
- Move your flight to different dates or times on the same route, starting from **Flight Options**.

## Booking reference or confirmation number?

Your booking confirmation email shows your Defera booking reference at the top. It's how we find your trip at Defera, but it isn't your airline confirmation number, and the airline can't look it up.

Your confirmation number comes later, once your trip is paid in full. Until then, a trip in **Trips** with a payment schedule and no confirmation number is completely normal.

> [!TIP] Paying ahead or paying off early gets your confirmation number to you sooner. [When do I get my confirmation number?](/help/confirmation-and-travel/when-confirmation-number) has the timing.`,
  },
  {
    slug: 'defera-installments',
    title: 'Defera Installments explained',
    summary: 'Pay a deposit when you book, then the rest in automatic weekly or bi-weekly payments. It works on any fare on the airlines we support, and on Flight + Hotel trips.',
    keywords: ['deposit', 'payment plan', 'weekly payments', 'bi-weekly payments', 'installment plan', 'buy now pay later', 'layaway'],
    related: ['getting-started/installments-vs-zero', 'booking/choosing-a-deposit', 'payments/how-installments-work', 'payments/change-schedule'],
    updated: '2026-10-05',
    body: `Defera Installments is our default way to pay for a trip over time. You pay a deposit when you book, and the rest of your trip total is split into automatic weekly or bi-weekly payments.

It works on any fare on the airlines we support, and on Flight + Hotel packages. There's no credit check and no interest, and your trip price is secured once your booking is approved.

## How it works

Here's what happens, from checkout to your confirmation number:

1. **You pay a deposit at checkout.** A deposit is the part of your trip price you pay upfront, when you book. Your deposit options depend on how far away your trip is, and your checkout page shows the options for your trip. Deposits can be as low as 5% on trips more than 90 days away.
2. **Your trip price is secured.** Once your booking is approved, your agreed-upon trip price is protected from future airfare increases while you stay in good standing, which means keeping up with your payments. So if airfares go up while you're paying, your price doesn't.
3. **The rest is charged automatically.** Your remaining balance is split into payments every week or every two weeks (bi-weekly). Each one is charged on its date to your card on file, which is the debit or credit card you save at checkout. Your trip's payment schedule shows what's coming up.
4. **You get your confirmation number.** Your airline confirmation number is released once your trip is paid in full. You can pay ahead or pay off early at any time to get it sooner.

## What does it cost?

Your trip total is the airfare, plus taxes, plus Defera's service fees. The **Price Breakdown** on your checkout page itemizes it before you book. There's no interest, no hidden fees and no penalty for paying off early. [What am I paying for? Your price breakdown](/help/getting-started/what-you-pay) goes through each line.

Defera Credit is credit on your Defera account that you can use toward a future booking. If you have some, it applies automatically at checkout, starting with your deposit. You can also choose to apply Defera Miles, which lowers your price before your payment plan is built.

## How far ahead can I book?

Every Defera trip departs at least {{PLAN_MIN_DAYS}} days after booking. The calendar in search starts there, so closer dates can't be booked.

Booking further ahead can lower your deposit. For example, if you book in January for a trip in June, your trip is more than 90 days away, so your deposit can be as low as 5%.

## Can I use it for a flight and hotel?

Yes. Flight + Hotel packages are paid with Defera Installments. Your deposit and payments cover the combined flight and hotel total, and everything else works the same way.

## How do I manage my payments?

Open your trip in **Trips**. The payment schedule, on the left on a computer or at the top on a phone, has what you need:

- **Make a Payment:** pay ahead, pay any extra amount, or pay off your trip in full.
- **Change Schedule:** switch between weekly and bi-weekly payments, with no fee, on plans that show this option.
- **Pick my own dates:** inside **Change Schedule**, where your trip offers it, choose when your next payment is due. It costs {{DATE_CHANGE_FEE}}, charged to your card on file when you confirm, and that one fee covers every date change you make that day.
- **Skip Payment:** skip your next payment for a {{SKIP_FEE}} fee, on plans that show this option. Your remaining payments go up slightly to cover it.
- **Update Card:** change the card your payments are charged to.

For example, if you pick a new date at noon and change it again at 4 p.m. the same day, you pay the {{DATE_CHANGE_FEE}} fee once. A change the next day is a new fee. Each date you pick needs to be at least {{DATE_CHANGE_CUTOFF_DAYS}} days before departure, and anything past due has to be paid first. [Picking your own payment dates](/help/payments/pick-your-dates) has the details.

Monthly payments aren't offered. Some older Defera Installments plans show **Request Extension** in place of **Change Schedule**.

## What if my plans change?

To cancel, open your trip in **Trips**, tap **Flight Options**, then **Cancel**.

- **Within 24 hours of booking:** your deposit is refunded in full to the card you paid with, automatically. For example, if you book at 3 p.m. on a Tuesday, you have until 3 p.m. on Wednesday.
- **After that:** your cancellation screen shows the options for your trip before you confirm anything. Defera Credit is offered on select bookings, and eligibility may vary.

A refunded deposit usually drops off your card statement within a day or so, as a pending charge that disappears. If the charge had already posted, the money returns to your card within a few business days. [What you get back when you cancel](/help/changes-cancellations/what-you-get-back) walks through each option.`,
  },
  {
    slug: 'defera-zero',
    title: 'Defera Zero explained',
    summary: 'Book with nothing due today on refundable fares from select airlines, then pay in equal payments. Cancel up to 24 hours before departure for 75% back.',
    keywords: ['$0 down', 'zero down', 'no deposit', 'nothing due today', 'refundable fare', '0% down', 'pay later'],
    related: ['getting-started/installments-vs-zero', 'getting-started/defera-installments', 'changes-cancellations/what-you-get-back', 'booking/which-airlines'],
    updated: '2026-10-05',
    body: `Defera Zero lets you book a flight with nothing due when you book. It's available on refundable fares from {{ZERO_AIRLINES}}.

Even with $0 down, your trip price is secured once your booking is approved. Your first payment isn't due at checkout. It's due on the first date in your payment schedule.

## How it works

Here's what happens when you book with Defera Zero:

1. **You book with $0 down.** Nothing is due at checkout. The card you enter is saved as your card on file, and your payments are charged to it.
2. **Your trip price is secured.** Once your booking is approved, your agreed-upon trip price is protected from future airfare increases while you stay in good standing, which means keeping up with your payments.
3. **You pay in equal payments.** Your trip total is split into equal weekly or bi-weekly payments, charged automatically to your card on file. The first one is due on the first date in your payment schedule.
4. **You get your confirmation number.** Your airline confirmation number is released once your trip is paid in full.

There are no surprise charges. The service fee and hold fee are built into your payment schedule, and there's no interest. As with every Defera plan, there's no credit check.

## Why don't I see Defera Zero?

Defera Zero works only on refundable fares, which are fares the airline lets you cancel for your money back. Many lower-priced fares aren't refundable, so you'll see Defera Zero on some flights and not others.

If you don't see it at checkout, check these:

- The fare isn't refundable.
- The airline isn't one that offers Defera Zero: {{ZERO_AIRLINES}}.
- You're booking a Flight + Hotel package. Those are paid with Defera Installments.

If Defera Zero isn't offered, [Defera Installments](/help/getting-started/defera-installments) works on any fare on the airlines we support, with a deposit at checkout.

## What if I need to cancel?

Open your trip in **Trips**, tap **Flight Options**, then **Cancel**. Your cancellation screen shows your options and the exact amounts before you confirm anything.

- **Before your first payment:** tap **Cancel Trip**. Your plan is cancelled and nothing is charged.
- **After your first payment:** tap **Cancel & receive 75% refund**. The screen shows the exact amounts and asks you to confirm, and then the refund runs automatically to the card you paid with. This option is there until 24 hours before departure, even once your trip is paid in full.
- **If you paid entirely with Defera Credit:** there's no card payment to refund, so cancelling returns it all as Defera Credit.

Refunds go back to the card you paid with. Your bank typically takes 5–10 business days to show the money, sometimes less.

## Can I get Defera Credit instead?

Some trips can also be cancelled for Defera Credit worth 100% of what you've paid. Defera Credit is credit on your Defera account. It never expires, and you can use it toward any future Defera booking, including trips for someone else. Defera Credit is offered on select bookings, and eligibility may vary.

If your trip offers it, your cancellation screen shows both choices, with the cash option as **Prefer cash? Take 75% back instead**. The credit is the larger amount, but it can't be turned into cash later, so choose the one that fits your plans.

For example, say you've paid $400 so far and you cancel two weeks before departure. The cash option returns $300 to your card. If your trip offers Defera Credit, you could choose $400 in Defera Credit instead.

[What you get back when you cancel](/help/changes-cancellations/what-you-get-back) covers every option in more detail.`,
  },
  {
    slug: 'installments-vs-zero',
    title: 'Installments or Defera Zero: which should I choose?',
    summary: 'Defera Zero has nothing due today, on refundable fares from select airlines. Installments takes a deposit but works on any fare, and on Flight + Hotel trips.',
    keywords: ['compare plans', 'which plan', 'payment options', 'zero down or deposit', 'refundable fare', 'difference between plans'],
    related: ['getting-started/defera-installments', 'getting-started/defera-zero', 'booking/choosing-a-deposit', 'changes-cancellations/what-you-get-back'],
    updated: '2026-10-05',
    body: `Choose Defera Zero if you'd like nothing due when you book and your fare qualifies. Choose Defera Installments for any other fare, or for a Flight + Hotel package.

Both plans let you book now and pay over time, with no credit check and no interest. Both secure your trip price once your booking is approved. The differences are what's due today, which fares qualify and what you get back if you cancel.

## How do the plans compare?

A deposit is the part of your trip price you pay upfront, when you book. Here's how the two plans line up:

| | Defera Installments | Defera Zero |
|---|---|---|
| Due when you book | A deposit, as low as 5% on trips more than 90 days away | Nothing |
| Which fares | Any fare on the airlines we support | Refundable fares on {{ZERO_AIRLINES}} |
| Your payments | Automatic, weekly or bi-weekly | Automatic and equal, weekly or bi-weekly |
| Flight + Hotel packages | Yes | No |
| Defera Miles at checkout | Yes | Yes |
| Cancelling later | Your cancellation screen shows the options for your trip | 75% of what you've paid back, up to 24 hours before departure |
| Cancel for Defera Credit | On select bookings | On select bookings |

The takeaway: Defera Zero costs nothing today and includes a cash option if your plans change, but it works only on certain fares. Defera Installments asks for a deposit and works on any fare on the airlines we support.

## What if my plans change?

This is where the plans differ most. Either way, your cancellation screen shows your options and the exact amounts before you confirm anything.

- **Defera Installments:** if you cancel within 24 hours of booking, your deposit is refunded in full to your card, automatically. After that, your cancellation screen shows the options for your trip.
- **Defera Zero:** if you cancel before your first payment, nothing is charged. After your first payment, you can get 75% of what you've paid back to your card, up to 24 hours before departure.
- **Both plans:** some trips can be cancelled for Defera Credit worth 100% of what you've paid. Defera Credit is offered on select bookings, and eligibility may vary.

For example, say you're on Defera Zero, you've paid $400 so far, and you cancel two weeks before departure. You'd get $300 back to your card, or $400 in Defera Credit if your trip offers it.

Defera Credit is credit on your Defera account for a future booking. It never expires, but it can't be exchanged for cash. [What you get back when you cancel](/help/changes-cancellations/what-you-get-back) explains every option.

## Which one fits my trip?

### Defera Zero may suit you if

- You'd rather pay nothing today.
- Your fare is refundable and on one of these airlines: {{ZERO_AIRLINES}}.
- You're booking a flight only.
- You'd like the option of cash back if your plans change: 75% of what you've paid, up to 24 hours before departure.

### Defera Installments may suit you if

- The fare you want isn't refundable, or it's on an airline that doesn't offer Defera Zero.
- You're booking a Flight + Hotel package.
- You're comfortable paying a deposit when you book.

## How do I compare them for my own trip?

Checkout offers Defera Zero only when your fare qualifies. When it does, your checkout page shows what's due today, your payments and your trip total for the plan you pick, so you can compare the two before you book.

> [!NOTE] Your checkout page and your trip page show the exact figures and cancellation options for your own trip. Where they differ from this overview, your trip's figures are the ones that apply.`,
  },
  {
    slug: 'credit-check',
    title: 'Is there a credit check?',
    summary: 'No. Defera never checks your credit: no score, and no hard or soft pull. We don’t report your payments to credit bureaus either, so it won’t build credit.',
    keywords: ['credit score', 'hard pull', 'soft pull', 'credit report', 'bad credit', 'no credit', 'credit bureaus', 'build credit'],
    related: ['getting-started/who-can-book', 'getting-started/is-defera-safe', 'missed-payments/what-is-default', 'getting-started/what-is-defera'],
    updated: '2026-10-23',
    body: `No. Defera doesn't check your credit to let you book. There's no credit score involved, and no hard or soft credit pull.

That's on purpose. We built Defera for people that credit-based travel financing leaves out, including gig workers, students, people rebuilding their credit and families on a tight budget. Your credit history doesn't come into it.

## What's a hard or soft credit pull?

Many financing and pay-over-time companies look at your credit report before they approve you. A hard pull is a full credit check that shows up on your credit report and can lower your score a little. A soft pull is a lighter check that doesn't affect your score.

Defera does neither, whichever plan you choose.

## Will paying with Defera build my credit?

No. We don't report your payment plan or your on-time payments to the credit bureaus, the companies that keep credit reports. So paying on time with Defera won't help build your credit.

Here's how it works at each stage:

- **When you book:** there's no credit pull, hard or soft.
- **When you pay on time:** we don't report your plan or your on-time payments to the credit bureaus.
- **If a plan goes unpaid:** what happens is set out in our [Terms of Service](/terms).

## Is there interest?

No. There's no interest on any Defera plan, and no hidden fees. The price you see at checkout is the price you pay, and the **Price Breakdown** on your checkout page lists every part of it before you book.

## What do I need instead of good credit?

To book, you need two things:

- **A debit or credit card for your payments.** You don't need a credit card: a debit card works too, including the one your bank already issued you. Prepaid cards aren't accepted on payment plans.
- **Each traveler's details,** such as their full legal name and date of birth.

[Who can book with Defera?](/help/getting-started/who-can-book) lists everything to have ready.

## Questions?

[Chat with the Defera Concierge](/support), our AI concierge, or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'who-can-book',
    title: 'Who can book with Defera?',
    summary: 'You don’t need good credit. You need a debit or credit card, and each traveler’s details as shown on their ID, plus passport details for international trips.',
    keywords: ['requirements', 'what do I need', 'eligibility', 'bad credit', 'debit card', 'passport', 'book for someone else'],
    related: ['booking/traveler-information', 'payments/accepted-payment-methods', 'getting-started/credit-check', 'booking/booking-for-others'],
    updated: '2026-10-23',
    body: `You don't need good credit to book with Defera, because there's no credit check. What you need is a debit or credit card for your payments, and some details for each traveler.

It helps to have both ready before you start. Here's what to gather.

## What kind of card do I need?

Defera takes debit and credit cards. Every payment plan needs a card on file. That's the card saved for your plan, and your payments are charged to it automatically on their dates.

- **Prepaid cards aren't accepted** on payment plans. That includes gift cards and other stored-value cards.
- **Other ways to pay aren't accepted.** That means bank transfers and ACH, PayPal, Venmo, Cash App, checks and cryptocurrency. Apple Pay and Google Pay aren't offered at checkout.
- **Want to pay from your bank account?** A debit card for that account works, including the one your bank already issued you.

If your card doesn't go through at checkout, check your card details first. If it's a prepaid card, try a debit or credit card instead: prepaid cards aren't accepted on payment plans, and the message you see doesn't always say so. If it still won't work, contact us so our team can take a look.

## What details do I need for each traveler?

Enter each traveler's details exactly as they appear on the government ID they'll travel with. The name on the booking needs to match that ID.

- **For every trip:** each traveler's full legal name, date of birth and gender, plus contact details for at least one traveler.
- **For international trips:** each traveler's passport number, passport expiration date and nationality.
- **Optional:** frequent flyer numbers, a Known Traveler Number, and special requests, such as meals or wheelchair assistance.

[What traveler information do I need?](/help/booking/traveler-information) goes into more detail.

## Can I book for other people?

Yes. You can book for yourself, for other people, or both, and family and group bookings are supported. Enter each person's details as they appear on their ID.

If you book for the same people often, save them in the Passengers section of your Profile. Next time, you can choose them from a dropdown and their details fill in for you.

## How far ahead do I need to book?

At least {{PLAN_MIN_DAYS}} days before departure. The calendar in search starts there, so closer dates can't be booked on Defera. [How far ahead do I need to book?](/help/getting-started/how-far-ahead-to-book) has the details.

## Questions?

[Chat with the Defera Concierge](/support), our AI concierge, or call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7. You can also send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'how-far-ahead-to-book',
    title: 'How far ahead do I need to book?',
    summary: 'Book at least {{PLAN_MIN_DAYS}} days before departure, since closer dates can’t be booked. Deposits can be as low as 5% on trips more than 90 days away.',
    keywords: ['last minute', 'book in advance', 'lead time', 'days before departure', 'earliest date', 'short notice', 'deposit'],
    related: ['booking/booking-close-to-departure', 'booking/choosing-a-deposit', 'payments/pay-off-early', 'getting-started/defera-installments'],
    updated: '2026-10-05',
    body: `Book at least {{PLAN_MIN_DAYS}} days before your trip. Every Defera trip departs at least {{PLAN_MIN_DAYS}} days after booking, so closer dates can't be booked, even if you'd like to pay for the whole trip at once.

Booking further ahead can also lower what you pay upfront. Deposits can be as low as 5% on trips more than 90 days away.

## What will I see in search?

When you open the calendar in search, the first departure date you can choose is {{PLAN_MIN_DAYS}} days from today. Earlier dates can't be selected.

This is the same on every plan, including Defera Zero and Flight + Hotel packages. [Can I book a trip that leaves soon?](/help/booking/booking-close-to-departure) has more on what you'll see.

## How does timing affect my deposit?

A deposit is the part of your trip price you pay upfront, when you book with Defera Installments. How far away your trip is shapes your deposit options:

| When your trip leaves | What to expect |
|---|---|
| Less than {{PLAN_MIN_DAYS}} days away | It can't be booked on Defera |
| {{PLAN_MIN_DAYS}} days or more away | A deposit at checkout (nothing with Defera Zero), then automatic weekly or bi-weekly payments |
| More than 90 days away | Deposits can be as low as 5% |

So if a low deposit matters to you, booking early helps. For example, if you book in January for a trip in June, your trip is more than 90 days away, and your deposit can be as low as 5%.

Your exact options depend on how far away your trip is, and your checkout page shows them before you book. [Choosing your deposit](/help/booking/choosing-a-deposit) explains how to pick one.

## What about Defera Zero?

Defera Zero has nothing due at checkout, so there's no deposit to choose. It's offered on refundable fares from select airlines, and your checkout page shows it when your trip qualifies. The same {{PLAN_MIN_DAYS}}-day rule applies. [Defera Zero explained](/help/getting-started/defera-zero) has the details.

## Can I get my confirmation number sooner?

Yes. Your airline confirmation number is released once your trip is paid in full, so paying ahead or paying off early gets it to you sooner. You can do either at any time with **Make a Payment** on your trip, and there's no penalty for paying early.`,
  },
  {
    slug: 'what-you-pay',
    title: 'What am I paying for? Your price breakdown',
    summary: 'Your trip total is the airfare plus taxes and Defera’s service fees, with no interest. The Price Breakdown at checkout lists every part of it before you book.',
    keywords: ['fees', 'service fee', 'hold fee', 'taxes', 'trip total', 'hidden fees', 'cost', 'why is the total higher'],
    related: ['payments/fees-explained', 'booking/checkout-page', 'payments/trip-statement', 'getting-started/price-protection'],
    updated: '2026-10-05',
    body: `Your trip total is the airfare, plus taxes, plus Defera's service fees. There's no interest and there are no hidden fees: nothing is added to your trip total beyond what your Price Breakdown shows.

You can see every part of it before you book, in the **Price Breakdown** on your checkout page. After you book, your trip's Trip Statement shows the pricing for that trip.

## What's in the Price Breakdown?

The **Price Breakdown** is open by default on your checkout page, and you can collapse it and open it again. Here's what each line means:

- **Base fare:** your airfare, after any airline travel credits or airline gift cards you've applied. Those show as "Credits Applied".
- **Service fee:** covers dedicated support throughout your payment plan.
- **Seat selection:** seats you chose at checkout, if any. Seats included with your fare show as "Free". Seats you pick here are part of your trip total, so they're paid over time with the rest of your trip.
- **Subtotal (before hold):** everything above, before the hold fee.
- **Hold fee:** covers the cost of letting you pay over time.
- **Trip total:** the full price of your trip.

Each fee has a small info icon with a one-line explanation, so you can check any line as you go. The amounts in your own breakdown are the ones that apply to your booking.

## Do prices in search include everything?

Yes, by default. Prices in search results and booking options are all-in: the fare includes airline taxes and carrier fees, and the trip total includes Defera's service fee and hold fee. The price you see is the full price of the flight, not a starting price that grows later.

If you'd rather browse base fares, turn off the **Grand total — taxes & fees included** toggle. Base fares are labeled "fare", never "total". Your checkout page shows your full trip total, whichever way the toggle is set.

Flight + Hotel packages are the one exception: some hotels collect fees at check-in. Those are labeled on your trip.

## Where can I see my costs after I book?

Your Trip Statement shows the pricing for your trip, along with your payment history. To download it, open the trip in **Trips** and tap the download icon in the trip's header row. It's a print-ready PDF, so you can save it or print it.

## What costs might come later?

Some extras are paid separately from your trip total, and only if you choose them:

- **Seats after booking.** Seats bought with **Purchase Seats** are paid in full by card, with a small processing fee shown before you pay. They aren't added to your payment plan.
- **Checked bags.** Bags added with **Purchase Baggage** are paid by card in one go, separately from your payment plan.
- **Changes to your schedule.** **Skip Payment** ({{SKIP_FEE}}) and picking your own payment dates ({{DATE_CHANGE_FEE}}) have their own fees.

[Fees, explained](/help/payments/fees-explained) lists every fee in one place.

## What if my total looks wrong?

If a line is unclear, tap its info icon on the checkout page for a one-line explanation. If you think your trip total is actually wrong, [chat with the Defera Concierge](/support), our AI concierge. It can send a request to our team to look into it.`,
  },
  {
    slug: 'price-protection',
    title: 'How your trip price is protected',
    summary: 'Once your booking is approved, your trip price is secured. If airfares go up, yours doesn’t, as long as you stay in good standing on your payment plan.',
    keywords: ['price lock', 'locked price', 'fare goes up', 'airfare increase', 'price change', 'secured price', 'price freeze'],
    related: ['confirmation-and-travel/price-protection-and-confirmation', 'missed-payments/good-standing', 'confirmation-and-travel/when-confirmation-number', 'getting-started/what-you-pay'],
    updated: '2026-10-05',
    body: `Once your booking is approved, your trip price is secured. If airfares go up after that, your price doesn't go up with them, as long as you stay in good standing on your payment plan.

This is what we call price protection. It works the same way on Defera Installments and Defera Zero, and you don't need to do anything to turn it on. It starts long before your confirmation number arrives.

## What does it mean in practice?

Airfares often change between the day you book and the day you fly. With price protection, those increases don't reach your trip.

For example, say your trip total is $600 when you book. A few weeks later, the same flights cost more. Your trip total stays $600, as long as you're keeping up with your payment plan.

## When does it start?

It starts once your booking is approved:

- **Defera Installments:** once your deposit is paid and your booking is approved.
- **Defera Zero:** once your booking is approved, with $0 down.

Your checkout page shows this as "Price protected from today".

## Can the price change before I book?

Yes. Fares can change until you book, so a trip you're still looking at isn't protected yet:

- **Leaving a trip at checkout** doesn't hold the price.
- **Email me this plan**, under the pay button, sends you the figures on your screen with a link back to the same checkout, but it doesn't hold the price either.
- **Reminder emails** about a trip you didn't finish booking don't hold the price.

Your price is secured once you book and your booking is approved.

## How do I keep my price protected?

Your price stays protected while you're in good standing on your payment plan. Good standing means keeping up with your payments.

Most of that happens on its own, because your payments are charged automatically to your card on file, the debit or credit card saved for your payments. A few things help:

- **Keep your card up to date.** If your card expires or is replaced, tap **Update Card** on your trip.
- **Catch up if a payment doesn't go through.** You can pay what's past due from **Make a Payment** on your trip.
- **Adjust before a payment is due.** Options like **Change Schedule** and **Skip Payment** can help, on plans that show them.

[What "good standing" means](/help/missed-payments/good-standing) has more.

## Is price protection the same as my confirmation number?

No. They're two different things, and they arrive at different times:

| | Price protection | Confirmation number |
|---|---|---|
| What it is | Your agreed-upon trip price, protected from future airfare increases | The airline's code for your reservation, which you use to check in |
| When you get it | Once your booking is approved | Once your trip is paid in full |
| What it needs from you | Staying in good standing on your payment plan | Paying your trip in full |

So until your trip is paid in full, it's normal for your trip in **Trips** to show a payment schedule and no confirmation number. Your price is already protected. Paying ahead or paying off early gets your confirmation number sooner, and [When do I get my confirmation number?](/help/confirmation-and-travel/when-confirmation-number) has the timing.`,
  },
  {
    slug: 'is-defera-safe',
    title: 'Is Defera safe and legitimate?',
    summary: 'Founded in 2024 in San Francisco, Defera encrypts your connection and never stores your complete card details. Our AI concierge answers 24/7.',
    keywords: ['scam', 'legit', 'trustworthy', 'secure', 'real company', 'is it safe', 'trust'],
    related: ['account/payment-security', 'account/privacy-and-data', 'emails/is-this-email-from-defera', 'contact/how-to-contact'],
    updated: '2026-10-23',
    body: `Defera is a travel company, founded in 2024 in San Francisco, that lets you book flights, or a flight and hotel together, and pay over time. We protect your payments with an encrypted connection, and we never store your complete card details.

It's a smart question to ask before you share your card details with anyone. Here's how we protect you, what we promise on every booking, and how to check that you're really dealing with us.

## Who is Defera?

We started Defera in San Francisco so that travel doesn't depend on perfect credit or a big bank balance. You can book with no credit check and no interest, and pay for your trip in automatic weekly or bi-weekly payments. You can read more about us on our [About page](/about).

## How are my payment details protected?

- **Your connection is encrypted.** Your connection to Defera is TLS-encrypted, which means the details you send are scrambled on their way to us.
- **We never store your complete card details.** Payments are processed by certified PCI-DSS payment gateways that use tokenization. PCI-DSS is the card industry's security standard, and tokenization means a secure token stands in for your card number.
- **Saved cards are replaced, not edited.** A saved card can't be edited in place. **Update Card**, inside your trip, takes the full card details in one secure flow and replaces the old card.

[How we protect your payment details](/help/account/payment-security) has more.

## What do you promise on every booking?

- **No credit check.** We never check your credit to let you book.
- **No interest and no hidden fees.** The **Price Breakdown** at checkout itemizes your trip total before you book, so you see exactly what you'll pay.
- **Your price is secured.** Once your booking is approved, your agreed-upon trip price is protected from future airfare increases while you stay in good standing on your payment plan.
- **24 hours to change your mind.** If you cancel within 24 hours of booking, your deposit is refunded in full to the card you paid with, automatically.

Our [Terms of Service](/terms) set out how the service works.

## How can I tell it's really Defera?

- **Emails:** your booking confirmation email comes from bookings@mail.defera.app, the moment you book.
- **Calls:** when you call us, the Defera Concierge asks for your booking reference (or the email on your account) and the passenger's name before it shares any account details.

If an email doesn't look right, [How to tell an email is really from Defera](/help/emails/is-this-email-from-defera) shows what to check.

## How is my personal data handled?

We ask for the traveler details a booking needs and a card for your payments. Our [Privacy Policy](/privacy) explains in full how we handle your personal data.

## Where can I read more about your security?

Our [Security Center](/security) explains how we protect your account, your payments and your data, what we'll never ask you for, and how to report a security concern.

## How do I reach you?

[Chat with the Defera Concierge](/support), our AI concierge, or call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7. You can also send us a message from the [Contact page](/contact#message). Our team replies within 24 hours. Our mailing address is on our [Contact page](/contact).`,
  },
  {
    slug: 'defera-glossary',
    title: 'Defera terms, explained',
    summary: 'Plain-English meanings of the words you’ll see on Defera, from deposit, trip total and good standing to Defera Credit, Miles and your confirmation number.',
    keywords: ['glossary', 'definitions', 'what does it mean', 'terminology', 'meaning', 'dictionary', 'jargon'],
    related: ['getting-started/how-defera-works', 'getting-started/what-you-pay', 'changes-cancellations/what-is-defera-credit', 'rewards/how-miles-work'],
    updated: '2026-10-05',
    body: `Here's what the words you'll see around Defera mean, in plain English. Terms in bold are buttons and menus you'll tap in the app.

If you're new to paying for travel over time, start with the first table. It covers how your plan and your payments work.

## Plans and payments

| Term | What it means |
|---|---|
| Defera Installments | Defera Installments is our default plan: you pay a deposit when you book, and the rest is charged in automatic weekly or bi-weekly payments. It works on any fare on the airlines we support, and on Flight + Hotel packages. |
| Defera Zero | Defera Zero is a plan with nothing due when you book, paid in equal weekly or bi-weekly payments. It's available on refundable fares from {{ZERO_AIRLINES}}. |
| Refundable fare | A refundable fare is a fare the airline lets you cancel for your money back, and it's the only kind of fare Defera Zero works on. |
| Deposit | A deposit is the part of your trip price you pay upfront, when you book with Defera Installments. Your deposit options depend on how far away your trip is, and can be as low as 5% on trips more than 90 days away. |
| Payment | A payment, also called an installment, is one of the automatic charges that pay off your trip after you book. |
| Weekly or bi-weekly | Weekly payments are charged every week, and bi-weekly payments are charged every two weeks. |
| Card on file | Your card on file is the debit or credit card saved for your payment plan, and it's the card your payments are charged to. |
| Trip total | Your trip total is the full price of your trip: the airfare, plus taxes, plus Defera's fees. |
| **Price Breakdown** | The Price Breakdown is the panel on your checkout page that lists every part of your trip total. |
| Service fee | The service fee covers dedicated support throughout your payment plan, and it's included in your trip total. |
| Hold fee | The hold fee covers the cost of letting you pay over time, and it's included in your trip total. |
| Price protection | Price protection means that once your booking is approved, your agreed-upon trip price doesn't go up when airfares do, as long as you stay in good standing. |
| Good standing | Being in good standing means keeping up with your payment plan, and it's what keeps your trip price protected. [What "good standing" means](/help/missed-payments/good-standing) explains more. |
| Past due | A payment is past due when it was scheduled but didn't go through, and you can catch up from **Make a Payment** on your trip. |
| Paid in full | Your trip is paid in full once your whole trip total has been paid, and that's when your confirmation number is released. |

## Your trip

| Term | What it means |
|---|---|
| **Trips** | Trips is the page in the main menu that lists your bookings: upcoming, past and incomplete. You tap a trip to open it. |
| Payment schedule | Your payment schedule is the section of your trip page that shows your payments and holds the buttons for managing them. |
| **Make a Payment** | Make a Payment lets you pay ahead, pay any extra amount or pay off your trip in full. |
| **Change Schedule** | Change Schedule switches your payments between weekly and bi-weekly, with no fee, on plans that show this option. |
| **Pick my own dates** | Pick my own dates, inside **Change Schedule** where your trip offers it, lets you choose your next payment date for a {{DATE_CHANGE_FEE}} fee that covers every date change you make that day. |
| **Skip Payment** | Skip Payment skips your next payment for a {{SKIP_FEE}} fee, on plans that show this option. |
| **Update Card** | Update Card is where you change the card your payments are charged to, and it's the only place to do it. |
| **Flight Options** | Flight Options is the menu on your trip page for changing your flight, buying seats or bags, and cancelling. |
| Booking reference | Your booking reference is Defera's code for your trip, shown at the top of your booking confirmation email, and it isn't your airline confirmation number. |
| Confirmation number | Your confirmation number is the airline's code for your reservation, which you use to check in. It's released once your trip is paid in full and appears on your trip page. |
| Confirmation Number Release | A Confirmation Number Release is the request sent to our team for you, automatically, when your final payment goes through. It shows as a card on your trip page while our team works on it. |
| Trip Statement | Your Trip Statement is a PDF of your trip that you can download from your trip page, with your itinerary, travelers, pricing and payment history. |
| Flight + Hotel package | A Flight + Hotel package is a flight and hotel booked together as one trip, by tapping **Stays** when you search, and it's paid with Defera Installments. |
| Operated by | On a codeshare flight, one airline sells the seat and a partner airline flies the plane. You'll see "Operated by X as Y", which means X flies the plane and Y sold the seat. |

## Credit, rewards and help

| Term | What it means |
|---|---|
| Defera Credit | Defera Credit is credit on your Defera account that you can use toward any future Defera booking, for you or someone else. It never expires and can't be exchanged for cash. |
| Defera Miles | Defera Miles are rewards you earn on completed trips: 2 Miles per $1 on the final amount you pay for a trip, and more at higher Defera Status tiers. A Mile is worth {{MILES_TRIP_VALUE}} toward a Defera booking, or {{MILES_GIFT_CARD_VALUE}} as an e-gift card. |
| Defera Status | Defera Status is our tier program, with Member, Silver, Gold, Platinum and Diamond tiers. Your tier is based on the qualifying dollars you spend on completed trips in a calendar year. |
| Defera Concierge | The Defera Concierge is our AI concierge. You can [chat with it](/support), or call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where it answers by voice 24/7. |

> [!TIP] New to Defera? [How Defera works, step by step](/help/getting-started/how-defera-works) puts these terms together.`,
  },
];
